import { injectable } from "tsyringe";
import {
  LoginRequestInterface,
  ResetPasswordRequestInterface,
  ForgotPasswordRequestInterface,
  LogoutRequestInterface,
  EditPasswordRequestInterface,
  VerificationAccountRequestInterface
} from "@/data/payload/contracts/AuthRequest";
import { AuthApiRepository } from "@/app/infrastructures/repositories/api/AuthApiRepository";
import {
  AuthEntities,
  SetupPasswordValidation,
  TokenVerificationAccountEntities,
  Login2faEntity,
  OtpRequestResponseEntity
} from "@/domain/entities/Auth";
import {
  ResponsePayload,
  ResponsePayloadV2
} from "@/domain/entities/ResponsePayload";
import {
  SetupPasswordValidationApiRequest,
  DeviceAuthApiRequest,
  OtpRequest,
  VerificationOTPRequest,
  SetupPasswordRequest,
  Login2faApiRequest,
  OtpRequestLogin2faApiRequest,
  OtpVerifySkipApiRequest,
  OtpVerifyLogin2faApiRequest
} from "@/data/payload/api/AuthRequest";

@injectable()
export class AuthPresenter {
  private repository: AuthApiRepository;

  constructor(repository: AuthApiRepository) {
    this.repository = repository;
  }

  public onLogin(payload: LoginRequestInterface): Promise<AuthEntities> {
    return this.repository.login(payload);
  }

  public deviceAuth(payload: DeviceAuthApiRequest) {
    return this.repository.deviceAuth(payload);
  }

  public deviceAuthCheckApproval() {
    return this.repository.deviceAuthCheckApproval();
  }

  public forgotPassword(
    payload: ForgotPasswordRequestInterface
  ): Promise<ResponsePayload> {
    return this.repository.forgotPassword(payload);
  }

  public validateSetupPasswordLink(
    payload: SetupPasswordValidationApiRequest
  ): Promise<SetupPasswordValidation> {
    return this.repository.validateSetupPasswordLink(payload);
  }

  public resendSetupPasswordLink(
    payload: SetupPasswordValidationApiRequest
  ): Promise<ResponsePayloadV2> {
    return this.repository.resendSetupPasswordLink(payload);
  }

  public resetPassword(
    payload: ResetPasswordRequestInterface
  ): Promise<ResponsePayload> {
    return this.repository.resetPassword(payload);
  }

  public editPassword(
    payload: EditPasswordRequestInterface
  ): Promise<ResponsePayload> {
    return this.repository.editPassword(payload);
  }

  public logout(payload: LogoutRequestInterface): Promise<ResponsePayload> {
    return this.repository.logout(payload);
  }

  public verificationAccount(
    payload: VerificationAccountRequestInterface
  ): Promise<TokenVerificationAccountEntities> {
    return this.repository.verificationAccount(payload);
  }

  public requestOtp(
    payload: OtpRequest
  ): Promise<TokenVerificationAccountEntities> {
    return this.repository.requestOtp(payload);
  }

  public verificationOTP(payload: VerificationOTPRequest): Promise<boolean> {
    return this.repository.verificationOTP(payload);
  }

  public setupPassword(
    payload: SetupPasswordRequest
  ): Promise<ResponsePayload> {
    return this.repository.setupPassword(payload);
  }

  public login2fa(payload: Login2faApiRequest): Promise<Login2faEntity> {
    return this.repository.login2fa(payload);
  }

  public requestLogin2faOtp(payload: OtpRequestLogin2faApiRequest): Promise<OtpRequestResponseEntity> {
    return this.repository.requestLogin2faOtp(payload);
  }

  public verifyLogin2faSkip(payload: OtpVerifySkipApiRequest): Promise<AuthEntities> {
    return this.repository.verifyLogin2faSkip(payload);
  }

  public verifyLogin2fa(payload: OtpVerifyLogin2faApiRequest): Promise<AuthEntities> {
    return this.repository.verifyLogin2fa(payload);
  }
}
